import { Outlet, createRootRoute, redirect, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { Toaster } from 'sonner';
import { ChatLauncher } from '@/components/chat/ChatLauncher';
import { Sidebar } from '@/components/Sidebar';
import { TopBar } from '@/components/TopBar';
import { ChatThreadProvider } from '@/hooks/useChatThread';
import { ThemeProvider, useTheme } from '@/hooks/useTheme';
import { api } from '@/lib/api';

// The chat launcher is hidden unless VITE_CHAT_ENABLED=true (apps/web/.env.local
// or the build env). The provider stays mounted so chat code keeps compiling
// and turning it back on is a one-line env change.
const CHAT_ENABLED = import.meta.env.VITE_CHAT_ENABLED === 'true';

// Public, chrome-less routes that don't require a session.
const AUTH_ROUTES = new Set(['/login', '/signup', '/forgot-password', '/reset-password']);
// Of those, the ones a logged-in user should be bounced away from (the token
// flows — verify/reset — stay reachable even while authenticated).
const GUEST_ONLY_ROUTES = new Set(['/login', '/signup', '/forgot-password']);

export const Route = createRootRoute({
  // Auth gate: every navigation checks the session. Unauthenticated users are
  // sent to /login (except on the public auth pages); already-authenticated
  // users hitting login/signup/forgot are bounced to the dashboard.
  beforeLoad: async ({ location }) => {
    const isAuthRoute = AUTH_ROUTES.has(location.pathname);
    let user = null;
    try {
      user = await api.auth.me();
    } catch {
      user = null;
    }
    if (!user && !isAuthRoute) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is thrown by design
      throw redirect({ to: '/login' });
    }
    if (user && GUEST_ONLY_ROUTES.has(location.pathname)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack redirect() is thrown by design
      throw redirect({ to: '/' });
    }
    return { user };
  },
  component: RootLayout,
});

function RootLayout() {
  return (
    <ThemeProvider>
      <RootInner />
    </ThemeProvider>
  );
}

function RootInner() {
  const { resolvedTheme } = useTheme();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isAuthRoute = AUTH_ROUTES.has(pathname);

  if (isAuthRoute) {
    // Bare full-screen shell for login/signup — no sidebar/topbar/chat.
    return (
      <div className="min-h-screen w-full bg-background text-foreground">
        <Outlet />
        <Toaster theme={resolvedTheme} position="bottom-right" richColors closeButton />
      </div>
    );
  }

  return (
    <ChatThreadProvider>
      <div className="flex h-screen w-full bg-background text-foreground">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar />
          <main className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-7xl px-6 py-6">
              <Outlet />
            </div>
          </main>
        </div>
        {CHAT_ENABLED && <ChatLauncher />}
        <Toaster theme={resolvedTheme} position="bottom-right" richColors closeButton />
        {(import.meta as ImportMeta & { env: { DEV: boolean } }).env.DEV && (
          <TanStackRouterDevtools position="bottom-right" />
        )}
      </div>
    </ChatThreadProvider>
  );
}
